// Data hook for the AI prompting panel. Owns the fetch lifecycle for both
// `ai-prompts-overrides` (list active prompts + their overrides) and
// `ai-prompts-suggestions` (peer-review queue). Exposes imperative
// `refresh`, `saveOverride`, `revertOverride`, `createSuggestion`, `decide`
// methods that update local state optimistically + reload after each write.
//
// Kept narrow on purpose so the panel component stays presentational.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  listPrompts,
  listSuggestions,
  saveOverride as apiSaveOverride,
  revertOverride as apiRevertOverride,
  createSuggestion as apiCreateSuggestion,
  decideSuggestion as apiDecideSuggestion,
  type DecideArgs,
  type PromptRowDTO,
  type SuggestionDTO,
} from "@/lib/aiPrompting/client";

export type PanelData = {
  prompts: PromptRowDTO[];
  suggestions: SuggestionDTO[];
};

export type UseAiPromptingPanelDataResult = {
  data: PanelData;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  saveOverride: (promptKey: string, text: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  revertOverride: (promptKey: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  createSuggestion: (
    promptKey: string,
    suggestedText: string,
    note: string | null,
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  decide: (args: DecideArgs) => Promise<{ ok: true } | { ok: false; error: string }>;
};

const EMPTY: PanelData = { prompts: [], suggestions: [] };

export function useAiPromptingPanelData(enabled: boolean = true): UseAiPromptingPanelDataResult {
  const [data, setData] = useState<PanelData>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    const [pr, sg] = await Promise.all([listPrompts(), listSuggestions({ status: "open" })]);
    if (!mountedRef.current) return;
    if (!pr.ok) {
      setError(pr.error);
      setLoading(false);
      return;
    }
    if (!sg.ok) {
      setError(sg.error);
      setLoading(false);
      return;
    }
    setData({ prompts: pr.data.prompts, suggestions: sg.data.suggestions });
    setLoading(false);
  }, [enabled]);

  useEffect(() => {
    if (enabled) void refresh();
  }, [enabled, refresh]);

  const saveOverride = useCallback(
    async (promptKey: string, text: string) => {
      const r = await apiSaveOverride(promptKey, text);
      if (!r.ok) return { ok: false as const, error: r.error };
      await refresh();
      return { ok: true as const };
    },
    [refresh],
  );

  const revertOverride = useCallback(
    async (promptKey: string) => {
      const r = await apiRevertOverride(promptKey);
      if (!r.ok) return { ok: false as const, error: r.error };
      await refresh();
      return { ok: true as const };
    },
    [refresh],
  );

  const createSuggestion = useCallback(
    async (promptKey: string, suggestedText: string, note: string | null) => {
      const r = await apiCreateSuggestion(promptKey, suggestedText, note);
      if (!r.ok) return { ok: false as const, error: r.error };
      await refresh();
      return { ok: true as const };
    },
    [refresh],
  );

  const decide = useCallback(
    async (args: DecideArgs) => {
      const r = await apiDecideSuggestion(args);
      if (!r.ok) return { ok: false as const, error: r.error };
      await refresh();
      return { ok: true as const };
    },
    [refresh],
  );

  return useMemo(
    () => ({ data, loading, error, refresh, saveOverride, revertOverride, createSuggestion, decide }),
    [data, loading, error, refresh, saveOverride, revertOverride, createSuggestion, decide],
  );
}
